import { ConnectedStandardSolanaWallet } from '@privy-io/react-auth/solana';
import {
	AddressLookupTableAccount,
	Transaction,
	TransactionInstruction,
	VersionedTransaction,
} from '@solana/web3.js';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { BaseMessageSignerWalletAdapter } from '@solana/wallet-adapter-base';
import {
	VelocityClient,
	IWalletV2,
	PublicKey,
	SignedTxData,
	TxParams,
} from '@velocity-exchange/sdk';
import { FEE_PAYER_WALLET, feePayerSignTransaction } from './feePayerWallet';
import { FeePayerError } from 'src/constants/errors/FeePayerError';
import { capturePostHogEvent } from 'src/providers/posthog/posthogConfig';

export interface TimingCallbacks {
	onTxBuildingStart?: () => void;
	onTxBuildingEnd?: () => void;
	onFeePayerSigningStart?: () => void;
	onFeePayerSigningEnd?: () => void;
	onEmbeddedWalletSigningStart?: () => void;
	onEmbeddedWalletSigningEnd?: () => void;
	onSendTransactionStart?: () => void;
	onSendTransactionEnd?: () => void;
}

export interface UiEnhancedIWalletV2 extends IWalletV2 {
	walletType: 'privy' | 'authorityWithFeePayer' | 'authorityWithoutFeePayer';
	/**
	 * Whether signAndSendTransaction builds with the sponsor as payer. walletType
	 * alone can't tell — both embedded-wallet variants report 'privy'.
	 */
	usesFeePayer: boolean;
	signAndSendTransaction: (
		ixs: TransactionInstruction | TransactionInstruction[],
		txParams?: TxParams,
		timingCallbacks?: TimingCallbacks,
		lookupTables?: AddressLookupTableAccount[]
	) => Promise<string>;
	buildAndSignTransactionsMap: <T extends string>(
		instructionsMap: Partial<
			Record<T, TransactionInstruction | TransactionInstruction[]>
		>,
		txParams?: TxParams
	) => Promise<{
		signedTxData: SignedTxData[];
		signedTxMap: Partial<Record<T, Transaction | VersionedTransaction>>;
	}>;
}

export function convertPrivyEmbeddedWalletToIWalletV2(
	embeddedWallet: ConnectedStandardSolanaWallet,
	velocityClient: VelocityClient,
	useFeePayer = true
): UiEnhancedIWalletV2 {
	const chain =
		Env.sdkEnv === 'mainnet-beta' ? 'solana:mainnet' : 'solana:devnet';

	// This mutex ensures only one transaction is being signed at a time. By assigning the current
	// action promise to this mutex variable, subsequent actions will need to await for the current
	// action promise to be resolved, before they can be executed.
	let mutexPromise: Promise<unknown> = Promise.resolve();

	const signTransaction = async (
		transaction: Transaction | VersionedTransaction,
		timingCallbacks?: Pick<
			TimingCallbacks,
			'onEmbeddedWalletSigningStart' | 'onEmbeddedWalletSigningEnd'
		>
	) => {
		timingCallbacks?.onEmbeddedWalletSigningStart?.();
		try {
			const signTxnResult = await embeddedWallet.signTransaction({
				transaction: transaction.serialize() as Uint8Array,
				chain,
			});

			const signedTxn = VersionedTransaction.deserialize(
				signTxnResult.signedTransaction
			);

			timingCallbacks?.onEmbeddedWalletSigningEnd?.();
			return signedTxn;
		} catch (e) {
			timingCallbacks?.onEmbeddedWalletSigningEnd?.();
			throw e;
		}
	};

	const embeddedIWallet: IWalletV2 = {
		publicKey: new PublicKey(embeddedWallet.address),
		signMessage: async (message: Uint8Array) => {
			return embeddedWallet
				.signMessage({ message })
				.then((result) => result.signature);
		},
		// @ts-expect-error - Allow return of VersionedTransaction
		signTransaction,
		// @ts-expect-error - Allow return of VersionedTransaction[]
		signAllTransactions: async (
			transactions: Transaction[] | VersionedTransaction[]
		) => {
			return Promise.all(transactions.map((tx) => signTransaction(tx)));
		},
	};

	const signAndSendTransaction = async (
		ixs: TransactionInstruction | TransactionInstruction[],
		txParams?: TxParams,
		timingCallbacks?: TimingCallbacks,
		lookupTables?: AddressLookupTableAccount[]
	): Promise<string> => {
		mutexPromise = mutexPromise
			.then(async () => {
				const initialTxHandlerWallet = velocityClient.txHandler.getWallet();

				try {
					if (useFeePayer) {
						// update the tx handler to use the fee payer while building/signing,
						// then switch back to embedded wallet for sending.
						velocityClient.txHandler.updateWallet(FEE_PAYER_WALLET); // driftClient.buildTransaction refers to the wallet at this step as the fee payer
						timingCallbacks?.onTxBuildingStart?.();
						const unsignedTransaction = await velocityClient.buildTransaction(
							ixs,
							txParams,
							0,
							lookupTables
						);
						timingCallbacks?.onTxBuildingEnd?.();

						const feePayerSignedTxnPromise = feePayerSignTransaction(
							unsignedTransaction,
							timingCallbacks
						);
						const embeddedSignedTxnPromise = signTransaction(
							unsignedTransaction,
							timingCallbacks
						);

						// sign with both signers in parallel
						const [feePayerSignedTxn, embeddedSignedTxn] = await Promise.all([
							feePayerSignedTxnPromise,
							embeddedSignedTxnPromise,
						]);

						// combine the signatures
						const combinedSignatures = [
							feePayerSignedTxn.signatures[0],
							embeddedSignedTxn.signatures[1],
						];

						const signedTransaction = feePayerSignedTxn;
						signedTransaction.signatures = combinedSignatures;

						velocityClient.txHandler.updateWallet(embeddedIWallet); // driftClient.sendTransaction refers to the wallet at this step as the main signer

						timingCallbacks?.onSendTransactionStart?.();
						const { txSig } = await velocityClient.sendTransaction(
							signedTransaction,
							undefined,
							undefined,
							true // we pre-signed the transaction in the signings step above
						);
						timingCallbacks?.onSendTransactionEnd?.();
						return txSig;
					}

					velocityClient.txHandler.updateWallet(embeddedIWallet);
					timingCallbacks?.onTxBuildingStart?.();
					const builtTxn = await velocityClient.buildTransaction(
						ixs,
						txParams,
						0,
						lookupTables
					);
					timingCallbacks?.onTxBuildingEnd?.();

					timingCallbacks?.onSendTransactionStart?.();
					const { txSig } = await velocityClient.sendTransaction(builtTxn);
					timingCallbacks?.onSendTransactionEnd?.();
					return txSig;
				} catch (e) {
					console.error(e);

					capturePostHogEvent('onboarding_privy_sign_send_error', {
						error_message: e instanceof Error ? e.message : String(e),
						error_stack: e instanceof Error ? e.stack : undefined,
						error_name: e instanceof Error ? e.name : undefined,
						error_raw: String(e),
						env: Env.sdkEnv,
						wallet_address: embeddedWallet.address,
						use_fee_payer: useFeePayer,
					});

					throw e;
				} finally {
					velocityClient.txHandler.updateWallet(initialTxHandlerWallet);
				}
			})
			.catch((e) => {
				// Re-throw to maintain error behavior
				throw e;
			});

		return mutexPromise as Promise<string>;
	};

	const buildAndSignTransactionsMap = async <T extends string>(
		instructionsMap: Partial<
			Record<T, TransactionInstruction | TransactionInstruction[]>
		>,
		txParams?: TxParams
	): Promise<{
		signedTxData: SignedTxData[];
		signedTxMap: Partial<Record<T, Transaction | VersionedTransaction>>;
	}> => {
		mutexPromise = mutexPromise
			.then(async () => {
				const initialTxHandlerWallet = velocityClient.txHandler.getWallet();

				try {
					if (useFeePayer) {
						velocityClient.txHandler.updateWallet(FEE_PAYER_WALLET); // driftClient.buildTransaction refers to the wallet at this step as the fee payer

						const txnsToSign = await velocityClient.buildTransactionsMap(
							instructionsMap as Record<
								string,
								TransactionInstruction | TransactionInstruction[]
							>,
							txParams
						);

						const entries = await Promise.all(
							Object.entries(txnsToSign).map(async ([key, txn]) => {
								if (!txn) {
									return [key, undefined] as const;
								}
								const signed = await feePayerSignTransaction(txn);
								return [key, signed] as const;
							})
						);
						const feePayerSignedTxns: Partial<
							Record<string, Transaction | VersionedTransaction>
						> = Object.fromEntries(entries);

						velocityClient.txHandler.updateWallet(embeddedIWallet); // driftClient.sendTransaction refers to the wallet at this step as the main signer

						const { signedTxData, signedTxMap } =
							await velocityClient.txHandler.getSignedTransactionMap(
								feePayerSignedTxns,
								embeddedIWallet
							);

						return { signedTxData, signedTxMap };
					}

					velocityClient.txHandler.updateWallet(embeddedIWallet);
					const txnsToSign = await velocityClient.buildTransactionsMap(
						instructionsMap as Record<
							string,
							TransactionInstruction | TransactionInstruction[]
						>,
						txParams
					);

					const { signedTxData, signedTxMap } =
						await velocityClient.txHandler.getSignedTransactionMap(
							txnsToSign as Partial<
								Record<string, Transaction | VersionedTransaction>
							>,
							embeddedIWallet
						);

					return { signedTxData, signedTxMap };
				} catch (e) {
					console.error(e);

					capturePostHogEvent('onboarding_privy_build_sign_error', {
						error_message: e instanceof Error ? e.message : String(e),
						error_stack: e instanceof Error ? e.stack : undefined,
						error_name: e instanceof Error ? e.name : undefined,
						error_raw: String(e),
						env: Env.sdkEnv,
						wallet_address: embeddedWallet.address,
						use_fee_payer: useFeePayer,
					});

					if (e instanceof FeePayerError) {
						throw e;
					}

					return { signedTxData: [], signedTxMap: {} };
				} finally {
					velocityClient.txHandler.updateWallet(initialTxHandlerWallet);
				}
			})
			.catch((e) => {
				// Re-throw to maintain error behavior
				throw e;
			});

		return mutexPromise as Promise<{
			signedTxData: SignedTxData[];
			signedTxMap: Partial<Record<T, Transaction | VersionedTransaction>>;
		}>;
	};

	return {
		...embeddedIWallet,
		signAndSendTransaction,
		buildAndSignTransactionsMap,
		walletType: 'privy',
		usesFeePayer: useFeePayer,
	};
}

export function convertSolanaWalletAdapterToIWalletV2(
	solanaWalletAdapter: BaseMessageSignerWalletAdapter,
	velocityClient: VelocityClient,
	useFeePayer: boolean
): UiEnhancedIWalletV2 {
	const walletType = useFeePayer
		? 'authorityWithFeePayer'
		: 'authorityWithoutFeePayer';
	const adapterIWallet: IWalletV2 = {
		publicKey: solanaWalletAdapter.publicKey as PublicKey,
		signMessage: async (message: Uint8Array) => {
			return solanaWalletAdapter.signMessage(message);
		},
		// @ts-expect-error - Allow return of VersionedTransaction
		signTransaction: async (
			transaction: Transaction | VersionedTransaction
		) => {
			return solanaWalletAdapter.signTransaction(transaction);
		},
		// @ts-expect-error - Allow return of VersionedTransaction[]
		signAllTransactions: async (
			transactions: Transaction[] | VersionedTransaction[]
		) => {
			return solanaWalletAdapter.signAllTransactions(
				transactions as VersionedTransaction[]
			);
		},
	};

	// This mutex ensures only one transaction is being signed at a time when using the fee payer.
	// By assigning the current action promise to this mutex variable, subsequent actions will need
	// to await for the current action promise to be resolved, before they can be executed.
	let feePayerWalletMutex: Promise<unknown> = Promise.resolve();

	const signAndSendTransaction = async (
		ixs: TransactionInstruction | TransactionInstruction[],
		txParams?: TxParams,
		timingCallbacks?: TimingCallbacks,
		lookupTables?: AddressLookupTableAccount[]
	): Promise<string> => {
		if (useFeePayer) {
			feePayerWalletMutex = feePayerWalletMutex
				.then(async () => {
					const initialTxHandlerWallet = velocityClient.txHandler.getWallet();

					try {
						velocityClient.txHandler.updateWallet(FEE_PAYER_WALLET);
						timingCallbacks?.onTxBuildingStart?.();
						const unsignedTransaction = await velocityClient.buildTransaction(
							ixs,
							txParams,
							0,
							lookupTables
						);
						timingCallbacks?.onTxBuildingEnd?.();

						// sign with both signers in parallel
						const [feePayerSignedTxn, adapterSignedTxn] = await Promise.all([
							feePayerSignTransaction(unsignedTransaction, timingCallbacks),
							solanaWalletAdapter.signTransaction(
								unsignedTransaction as VersionedTransaction
							),
						]);

						// combine the signatures
						const signedTransaction = feePayerSignedTxn as VersionedTransaction;
						signedTransaction.signatures = [
							feePayerSignedTxn.signatures[0],
							(adapterSignedTxn as VersionedTransaction).signatures[1],
						];

						velocityClient.txHandler.updateWallet(adapterIWallet);

						timingCallbacks?.onSendTransactionStart?.();
						const { txSig } = await velocityClient.sendTransaction(
							signedTransaction,
							undefined,
							undefined,
							true
						);
						timingCallbacks?.onSendTransactionEnd?.();
						return txSig;
					} catch (e) {
						console.error(e);

						if (e instanceof FeePayerError) {
							throw e;
						}

						throw e;
					} finally {
						velocityClient.txHandler.updateWallet(initialTxHandlerWallet);
					}
				})
				.catch((e) => {
					// Re-throw to maintain error behavior
					throw e;
				});

			return feePayerWalletMutex as Promise<string>;
		}

		timingCallbacks?.onTxBuildingStart?.();
		const builtTxn = await velocityClient.buildTransaction(
			ixs,
			txParams,
			0,
			lookupTables
		);
		timingCallbacks?.onTxBuildingEnd?.();
		timingCallbacks?.onSendTransactionStart?.();
		const { txSig } = await velocityClient.sendTransaction(builtTxn);
		timingCallbacks?.onSendTransactionEnd?.();
		return txSig;
	};

	const buildAndSignTransactionsMap = async <T extends string>(
		instructionsMap: Partial<
			Record<T, TransactionInstruction | TransactionInstruction[]>
		>,
		txParams?: TxParams
	): Promise<{
		signedTxData: SignedTxData[];
		signedTxMap: Partial<Record<T, Transaction | VersionedTransaction>>;
	}> => {
		if (useFeePayer) {
			feePayerWalletMutex = feePayerWalletMutex
				.then(async () => {
					const initialTxHandlerWallet = velocityClient.txHandler.getWallet();

					try {
						velocityClient.txHandler.updateWallet(FEE_PAYER_WALLET);

						const txnsToSign = await velocityClient.buildTransactionsMap(
							instructionsMap as Record<
								string,
								TransactionInstruction | TransactionInstruction[]
							>,
							txParams
						);

						const entries = await Promise.all(
							Object.entries(txnsToSign).map(async ([key, txn]) => {
								if (!txn) return [key, undefined] as const;
								const signed = await feePayerSignTransaction(txn);
								return [key, signed] as const;
							})
						);
						const feePayerSignedTxns: Partial<
							Record<string, Transaction | VersionedTransaction>
						> = Object.fromEntries(entries);

						velocityClient.txHandler.updateWallet(adapterIWallet);

						const { signedTxData, signedTxMap } =
							await velocityClient.txHandler.getSignedTransactionMap(
								feePayerSignedTxns,
								adapterIWallet
							);

						return { signedTxData, signedTxMap };
					} catch (e) {
						console.error(e);

						if (e instanceof FeePayerError) {
							throw e;
						}

						return { signedTxData: [], signedTxMap: {} };
					} finally {
						velocityClient.txHandler.updateWallet(initialTxHandlerWallet);
					}
				})
				.catch((e) => {
					// Re-throw to maintain error behavior
					throw e;
				});

			return feePayerWalletMutex as Promise<{
				signedTxData: SignedTxData[];
				signedTxMap: Partial<Record<T, Transaction | VersionedTransaction>>;
			}>;
		}

		const txnsToSign = await velocityClient.buildTransactionsMap(
			instructionsMap as Record<
				string,
				TransactionInstruction | TransactionInstruction[]
			>,
			txParams
		);

		const { signedTxData, signedTxMap } =
			await velocityClient.txHandler.getSignedTransactionMap(
				txnsToSign as Partial<
					Record<string, Transaction | VersionedTransaction>
				>
			);

		return { signedTxData, signedTxMap };
	};

	return {
		...adapterIWallet,
		signAndSendTransaction,
		buildAndSignTransactionsMap,
		walletType,
		usesFeePayer: useFeePayer,
	};
}
